import type { Route } from 'next';

export const FIX_TYPE = {
  SINGLE: 'single_fix',
  FIX_ALL: 'fix_all',
  CODE_SCAN_FIX: 'code_scan_fix',
  COMMAND: 'command',
  /** One auto-fix loop iteration: a batch of findings the loop dispatched on its own. */
  AUTO_FIX_LOOP: 'auto_fix_loop',
} as const;

export type FixType = (typeof FIX_TYPE)[keyof typeof FIX_TYPE];

export const FIX_ENTRY_SOURCE = {
  GITHUB_UI_TO_CUBIC_UI: 'github_ui_to_cubic_ui',
  CUBIC_UI_TO_CUBIC_UI: 'cubic_ui_to_cubic_ui',
  SLACK_UI_TO_CUBIC_UI: 'slack',
  DESKTOP_NOTIFICATION_TO_CUBIC_UI: 'desktop_notification',
  UNKNOWN: 'unknown',
} as const;

export type FixEntrySource = (typeof FIX_ENTRY_SOURCE)[keyof typeof FIX_ENTRY_SOURCE];

export const FIX_ENTRY_SOURCE_QUERY_PARAM = 'entrySource';

export function isFixEntrySource(value: string | null | undefined): value is FixEntrySource {
  return (
    value === FIX_ENTRY_SOURCE.GITHUB_UI_TO_CUBIC_UI ||
    value === FIX_ENTRY_SOURCE.CUBIC_UI_TO_CUBIC_UI ||
    value === FIX_ENTRY_SOURCE.SLACK_UI_TO_CUBIC_UI ||
    value === FIX_ENTRY_SOURCE.DESKTOP_NOTIFICATION_TO_CUBIC_UI ||
    value === FIX_ENTRY_SOURCE.UNKNOWN
  );
}

export function normalizeFixEntrySource(value: string | null | undefined): FixEntrySource {
  return isFixEntrySource(value) ? value : FIX_ENTRY_SOURCE.UNKNOWN;
}

export function getFixAnalyticsDetails(
  fixType?: FixType,
  batchFix?: { totalCount: number },
): { fixType: FixType; fixViolationCount: number | undefined } {
  const resolvedFixType = fixType ?? FIX_TYPE.COMMAND;
  const fixViolationCount =
    resolvedFixType !== FIX_TYPE.COMMAND ? (batchFix?.totalCount ?? 1) : undefined;
  return { fixType: resolvedFixType, fixViolationCount };
}

export function buildFixPRPath(params: {
  owner: string;
  repo: string;
  pullNumber: number | string;
  reviewSessionId: string;
  entrySource?: FixEntrySource;
  utmSource?: string;
}): Route {
  const path = `/action/fix/pr/${encodeURIComponent(params.owner)}/${encodeURIComponent(params.repo)}/${encodeURIComponent(String(params.pullNumber))}/${encodeURIComponent(params.reviewSessionId)}`;

  const searchParams = new URLSearchParams();
  if (params.entrySource && params.entrySource !== FIX_ENTRY_SOURCE.UNKNOWN) {
    searchParams.set(FIX_ENTRY_SOURCE_QUERY_PARAM, params.entrySource);
  }
  if (params.utmSource) {
    searchParams.set('utm_source', params.utmSource);
  }

  if (searchParams.size === 0) {
    return path as Route;
  }

  return `${path}?${searchParams.toString()}` as Route;
}
